import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActionSheetContent } from '@/components/action-sheet';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { MULTI_ORDER_GUIDE_QUERY } from '@/contexts/items-context-queries';
import {
  OrderGuide_DeleteCollectionMutation,
  OrderGuide_DeleteCollectionMutationVariables,
} from '@/types';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

export const DELETE_COLLECTION_MUTATION = gql`
  mutation OrderGuide_DeleteCollection(
    $collectionUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    deleteOrderGuideCollection(
      collection_id: $collectionUUID
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
    ) {
      success
    }
  }
`;

interface Props {
  collectionName: string;
  collectionUUID: string;
  deleteCollectionMode: 'delete' | 'reset';
  onCancel: () => void;
  onDelete: () => void;
}

export const DeleteCollectionConfirmation: React.FC<Props> = ({
  collectionName,
  collectionUUID,
  deleteCollectionMode,
  onCancel,
  onDelete,
}) => {
  const [loading, setLoading] = useState(false);
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { showErrorToast } = useToasts();
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useUserContext();

  const [deleteCollection] = useMutation<
    OrderGuide_DeleteCollectionMutation,
    OrderGuide_DeleteCollectionMutationVariables
  >(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(MULTI_ORDER_GUIDE_QUERY) as string],
  });

  return (
    <ActionSheetContent
      actions={[
        deleteCollectionMode === 'reset'
          ? i18n.t('reset_order_guide')
          : i18n.t('delete_order_guide'),
        i18n.t('cancel'),
      ]}
      loadingActionIndex={loading ? 0 : undefined}
      message={
        deleteCollectionMode === 'reset'
          ? i18n.t('reset_order_guide.message', {
              collectionName,
            })
          : i18n.t('delete_order_guide.message', {
              collectionName,
            })
      }
      onAction={async (_, idx) => {
        if (idx === 0) {
          try {
            setLoading(true);
            await deleteCollection({
              variables: {
                collectionUUID,
                restaurantUUID: selectedRestaurantUUID,
                supplierUUID: selectedRestaurantRelationship?.supplierUUID,
              },
            });
            orderGuideStoreApi
              .getState()
              .removeRestaurantCollection(collectionUUID);
            onDelete();
          } catch (e) {
            showErrorToast();
          } finally {
            setLoading(false);
          }
        } else {
          onCancel();
        }
      }}
      title={
        deleteCollectionMode === 'reset'
          ? i18n.t('reset_order_guide.title', {
              collectionName,
            })
          : i18n.t('delete_order_guide.title', {
              collectionName,
            })
      }
    />
  );
};
